import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

export default function Home() {
  useEffect(() => {
    // Schema.org JSON-LD for HomePage
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "株式会社OnYourSide",
      "url": `${import.meta.env.VITE_SITE_URL}/`,
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${import.meta.env.VITE_SITE_URL}/search?q={search_term_string}`,
        "query-input": "required name=search_term_string"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    {
      icon: 'ri-presentation-line',
      title: '企業向け研修プログラム',
      description: 'チームビルディングからリーダーシップ開発まで、企業のニーズに合わせたカスタム研修を提供します。'
    },
    {
      icon: 'ri-chat-3-line',
      title: 'コミュニケーション研修',
      description: '効果的なコミュニケーションスキルを身につけ、職場での関係性向上をサポートします。'
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'イノベーション研修',
      description: '創造的思考力を育成し、新しいアイデアを生み出すための研修プログラムです。'
    },
    {
      icon: 'ri-user-settings-line',
      title: '人事コンサルティング',
      description: '人事制度設計から組織開発まで、企業の人材戦略を包括的にサポートします。'
    }
  ];

  const stats = [
    { number: '300+', label: '研修実施実績' },
    { number: '95%', label: '受講者満足度' }
  ];

  const testimonials = [
    {
      name: 'T.K様',
      company: 'A商事株式会社',
      role: '人事部長',
      content: 'チーム力向上研修のおかげで、部署全体のコミュニケーションが格段に改善されました。実践的な内容で即座に職場で活用できています。',
      avatar: 'https://public.readdy.ai/ai/img_res/4b0377558d2165743caa7977e898899c.jpg'
    },
    {
      name: 'S.M様',
      company: 'D株式会社',
      role: 'マーケティング部長',
      content: 'リーダーシップ研修を受講して、チームマネジメントに自信を持てるようになりました。講師の丁寧な指導に感謝しています。',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20Japanese%20woman%20in%20the%2030s%20with%20black%20hair%2C%20friendly%20smile%2C%20business%20casual%20attire%2C%20modern%20office%20environment%2C%20confident%20and%20positive%20expression%2C%20Asian%20facial%20features&width=80&height=80&seq=testimonial2j&orientation=squarish'
    },
    {
      name: 'Y.H様',
      company: 'X製造業',
      role: '工場長',
      content: 'リーダーシップ研修により、部下との関係が改善され、チーム全体の生産性が向上しました。',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20Japanese%20middle-aged%20businessman%20with%20black%20hair%2C%20reliable%20expression%2C%20business%20shirt%20and%20tie%2C%20industrial%20office%20background%2C%20leadership%20qualities%20visible%2C%20Asian%20features&width=80&height=80&seq=testimonial3j&orientation=squarish'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url('https://readdy.ai/api/search-image?query=Modern%20corporate%20training%20seminar%20room%20with%20diverse%20Japanese%20professionals%20attending%20workshop%2C%20Asian%20businesswoman%20trainer%20presenting%20to%20Japanese%20audience%2C%20bright%20natural%20lighting%2C%20collaborative%20learning%20atmosphere%2C%20left%20side%20has%20subtle%20warm%20gradient%20overlay%20for%20text%20readability%2C%20inspiring%20business%20education%20environment%20in%20Japan&width=1920&height=1080&seq=heroj1&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              人材育成で<br />
              組織の未来を創る
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              実践的な研修・コンサルティングで、企業と組織の成長をサポート。経験豊富な講師が、組織の可能性を引き出します。
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
                onClick={() => window.location.href = 'mailto:info@oyside.com'}
              >
                お問い合わせ
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-gray-900 whitespace-nowrap">
                研修プログラムを見る
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              サービス一覧
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              企業のニーズに合わせた質の高いサービスを提供しています
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
              <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-lg mb-6">
                <i className="ri-presentation-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                トレーニング
              </h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                企業向け研修プログラムで、組織の成長と人材育成をサポートいたします。リーダーシップ研修、コンプライアンス研修、アサーティブコミュニケーション研修など。
              </p>
              <Link to="/services">
                <Button className="w-full whitespace-nowrap">
                  詳細を見る
                </Button>
              </Link>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
              <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-lg mb-6">
                <i className="ri-user-settings-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                コンサルティング
              </h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                人事制度設計から組織開発まで、企業の人材戦略を包括的にサポートします。採用支援、組織開発、ダイバーシティ推進など。
              </p>
              <Link to="/hr-consulting">
                <Button className="w-full whitespace-nowrap">
                  詳細を見る
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Company Philosophy Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              私たちが大切にしていること
            </h2>
            <p className="text-xl text-gray-600">
              私たちが大切にしている価値観とアプローチ
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-blue-100 rounded-full mb-6 mx-auto">
                <i className="ri-heart-3-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                きめ細やかなサポート
              </h3>
              <p className="text-gray-600 leading-relaxed">
                一人ひとりの個性と成長ペースに合わせた、丁寧で温かい指導を行います。
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-blue-100 rounded-full mb-6 mx-auto">
                <i className="ri-tools-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                実践重視のプログラム
              </h3>
              <p className="text-gray-600 leading-relaxed">
                理論だけでなく、実際の現場で活用できる実践的なスキルを重視した内容です。
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-blue-100 rounded-full mb-6 mx-auto">
                <i className="ri-team-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                継続的なフォローアップ
              </h3>
              <p className="text-gray-600 leading-relaxed">
                研修後も継続的にサポートし、学んだスキルの定着と発展をお手伝いします。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Representative Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://static.readdy.ai/image/2db098dc71e4ecf917d1962c25013c7b/bbca2c8f0455c7e0e3dae8cadb819389.jpeg"
                alt="代表者"
                className="rounded-xl shadow-lg object-cover object-top w-full h-96 lg:h-[500px]"
              />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                代表コンサルタントからのメッセージ
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                製造業、自治体、外資系金融機関、SIer、グローバルIT企業と、多様な組織でのHR業務経験を積んでまいりました。
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                外資系金融機関では年間200日以上の登壇経験を通じて、実践的な研修スキルを磨きました。人事部長として人事業務全般を管掌した経験から、組織の課題を深く理解しています。
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                様々な組織での実体験を踏まえた、分かりやすく実践的な研修で、企業様の成長をサポートしてまいります。
              </p>
              <Link to="/about">
                <Button variant="outline" className="whitespace-nowrap">
                  詳しいプロフィールを見る
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Training Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              研修の特徴
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-blue-100 rounded-full mb-6 mx-auto">
                <i className="ri-heart-3-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                きめ細やかなサポート
              </h3>
              <p className="text-gray-600 leading-relaxed">
                一人ひとりの個性と成長ペースに合わせた、丁寧で温かい指導を行います。
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-blue-100 rounded-full mb-6 mx-auto">
                <i className="ri-tools-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                実践重視のプログラム
              </h3>
              <p className="text-gray-600 leading-relaxed">
                理論だけでなく、実際の現場で活用できる実践的なスキルを重視した内容です。
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-blue-100 rounded-full mb-6 mx-auto">
                <i className="ri-team-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                継続的なフォローアップ
              </h3>
              <p className="text-gray-600 leading-relaxed">
                研修後も継続的にサポートし、学んだスキルの定着と発展をお手伝いします。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              受講者の声
            </h2>
            <p className="text-xl text-gray-600">
              実際に研修・コンサルティングを受けられた企業様からのお声をご紹介します
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg">
                <div className="flex items-center mb-6">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-gray-600">{testimonial.company}</p>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  "{testimonial.content}"
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Public Sector Services Section */}
      <section className="py-20 bg-gradient-to-r from-green-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              公務員向け特化サービス
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              公務員の皆様に特化した研修・コンサルティングサービスをご提供しています
            </p>
          </div>
          
          <div className="bg-white rounded-2xl p-12 shadow-lg">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  公務員の皆様へ
                </h3>
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start">
                    <i className="ri-check-double-line text-green-600 mt-1 mr-3"></i>
                    <span className="text-gray-700">公務員特有の課題に対応した専門研修</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-double-line text-green-600 mt-1 mr-3"></i>
                    <span className="text-gray-700">行政サービス向上のためのスキル開発</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-double-line text-green-600 mt-1 mr-3"></i>
                    <span className="text-gray-700">公務員倫理・コンプライアンス研修</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-double-line text-green-600 mt-1 mr-3"></i>
                    <span className="text-gray-700">住民対応力向上プログラム</span>
                  </li>
                </ul>
                <a 
                  href="https://public.oyside.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <Button size="lg" className="bg-green-600 hover:bg-green-700 whitespace-nowrap">
                    公務員向けサイトを見る
                  </Button>
                </a>
              </div>
              
              <div className="text-center">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20government%20office%20training%20session%20with%20diverse%20Japanese%20public%20servants%20attending%20workshop%2C%20female%20trainer%20presenting%20to%20civil%20servants%2C%20modern%20government%20building%20conference%20room%2C%20collaborative%20learning%20atmosphere%2C%20inspiring%20public%20service%20education%20environment%20in%20Japan&width=500&height=400&seq=publicj1&orientation=landscape"
                  alt="公務員向け研修"
                  className="rounded-xl shadow-lg object-cover w-full h-80"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            まずはお気軽にご相談ください
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            研修内容のご相談から実施まで、丁寧にサポートいたします。無料相談も承っております。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 whitespace-nowrap">
                お問い合わせ
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600 whitespace-nowrap">
              資料ダウンロード
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
